import { Link } from 'react-router-dom'
import Illustration from '@/components/Illustration'
import Card from '@/components/ui/Card'
import { sponsorsEnabled, nominationsEnabled } from '@/lib/feature-flags'
import { SponsorStartForm } from '@/sponsor/ClassroomProgramWidgets'

const steps = [
  {
    title: 'Sponsor',
    copy: 'Choose a classroom story that resonates and make a pledge in minutes.',
    illustration: 'books',
  },
  {
    title: 'Classroom',
    copy: 'Teachers receive funds, plan experiences, and keep everyone in the loop.',
    illustration: 'kids',
  },
  {
    title: 'Impact',
    copy: 'See progress, photos, and milestones that celebrate student growth.',
    illustration: 'leaf',
  },
]

export default function SponsorClassroomAbout() {
  return (
    <div className="space-y-16">
      <section className="grid items-center gap-8 md:grid-cols-2">
        <div className="space-y-6">
          <h1 className="text-4xl font-semibold leading-tight text-cocoa">
            Sponsor a Classroom, empower a future.
          </h1>
          <p className="text-lg text-cocoa/70">
            Project Tower connects sponsors with classrooms to fund supplies, experiences, and the tools that unlock student
            potential. The Smart Block Network keeps everyone aligned with clear milestones, transparent budgets, and real-time
            storytelling.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link className="btn" to="/sponsor/signup">
              Become a Sponsor
            </Link>
            <Link className="btn border border-sand bg-white text-cocoa hover:bg-shell" to="/sponsor-classroom/donate">
              Open Donations
            </Link>
            <Link className="btn bg-mint text-cocoa hover:bg-mint/80" to="/login?role=teacher">
              Teacher Login
            </Link>
          </div>
        </div>
        <Card className="relative overflow-hidden bg-gradient-to-br from-brick/10 via-sand to-mint/10">
          <div className="relative flex justify-center p-6">
            <Illustration name="teacher" className="h-56 w-56" animate />
          </div>
        </Card>
      </section>

      <section className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-2xl font-semibold text-cocoa">How it works</h2>
          <span className="text-sm uppercase tracking-wide text-cocoa/60">Sponsor → Classroom → Impact</span>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {steps.map(step => (
            <Card key={step.title} className="relative flex h-full flex-col gap-4 overflow-hidden">
              <div className="flex items-start gap-4">
                <Illustration name={step.illustration} className="h-20 w-20 flex-shrink-0" animate />
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-cocoa">{step.title}</h3>
                  <p className="text-sm leading-relaxed text-cocoa/70">{step.copy}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section className="space-y-4 rounded-3xl bg-shell/80 p-6 text-sm text-cocoa/80">
        <h2 className="text-xl font-semibold text-cocoa">Why classrooms love it</h2>
        <ul className="list-disc space-y-2 pl-5">
          <li>Flexible funding that keeps projects moving without red tape.</li>
          <li>Ongoing storytelling that invites families and partners into the journey.</li>
          <li>Reality Check fact lattice ensures every detail stays grounded and transparent.</li>
        </ul>
      </section>

      <section className="flex flex-wrap gap-3">
        <Link className="btn" to="/sponsor/signup">
          Become a Sponsor
        </Link>
        <Link className="btn bg-mint text-cocoa hover:bg-mint/80" to="/login?role=sponsor">
          Sponsor Login
        </Link>
      </section>

      {sponsorsEnabled() ? (
        <section className="grid gap-6 rounded-3xl border border-sand bg-white/80 p-6 shadow-soft md:grid-cols-2">
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-cocoa">Ready to sponsor?</h2>
            <p className="text-sm text-cocoa/70">
              Share your email and we&apos;ll send the $800 classroom sponsorship link plus FAQs.
            </p>
          </div>
          <SponsorStartForm />
        </section>
      ) : null}

      {nominationsEnabled() ? (
        <section className="space-y-6 rounded-3xl border border-sand bg-shell/60 p-6 shadow-soft">
          <div className="space-y-3">
            <h2 className="text-2xl font-semibold text-cocoa">Community nominations &amp; Graduation Ceremonies</h2>
            <p className="text-sm text-cocoa/70">
              Supporters nominate teachers they love. Teachers confirm with their school email to join the active pool. When
              sponsors mint new slots, Project Tower hosts a Graduation Ceremony that randomly pairs confirmed teachers with
              fresh classrooms to open.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="h-full space-y-2 bg-white/90 p-4">
              <h3 className="text-base font-semibold text-cocoa">1. Nominate a teacher</h3>
              <p className="text-sm text-cocoa/70">
                Anyone can share a teacher&apos;s name, email, school, and a note of encouragement. We forward the love directly to
                them.
              </p>
            </Card>
            <Card className="h-full space-y-2 bg-white/90 p-4">
              <h3 className="text-base font-semibold text-cocoa">2. Teacher confirms</h3>
              <p className="text-sm text-cocoa/70">
                Teachers secure their spot by confirming with a school or professional email. Confirmed teachers remain in the
                pool until they&apos;re selected.
              </p>
            </Card>
            <Card className="h-full space-y-2 bg-white/90 p-4">
              <h3 className="text-base font-semibold text-cocoa">3. Graduation Ceremony</h3>
              <p className="text-sm text-cocoa/70">
                Every sponsored slot opens two classrooms. Ceremonies randomize the confirmed pool to match teachers with fresh
                opportunities.
              </p>
            </Card>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link className="btn" to="/nominate">
              Nominate
            </Link>
            <p className="text-sm text-cocoa/70">
              Already nominated someone? Ask them to watch for a “You were nominated” email from Project Tower to confirm their
              classroom.
            </p>
          </div>
        </section>
      ) : null}
    </div>
  )
}
